import { useMemo } from "react";
import { useQuery } from "react-query";
import { Shop, TagsDocument } from "@/types/v2";
import { TagsRepository } from "@/repositories/tags";
import { AutomationRepository } from "@/repositories/automation";
import {
  auditAutomations,
  auditCardColors,
  auditEnabledFeatures,
  auditShopConfig,
  auditTags,
} from "@/lib/shopConfigAudit";
import { ShopAudit } from "@/lib/shopConfigAuditView";

export type ShopAuditRow = ShopAudit & { shop: Shop };

type Automations = Awaited<ReturnType<typeof AutomationRepository.getAll>>;

// The two per-shop reads that can't be derived from the shop document.
type ShopLists = {
  tagsDoc: TagsDocument | undefined;
  automations: Automations;
  fetchError?: string;
};

const fetchShopLists = async (shop: Shop): Promise<ShopLists> => {
  try {
    const [tagsDoc, automations] = await Promise.all([
      TagsRepository.get(shop.id),
      AutomationRepository.getAll(shop.id),
    ]);
    return { tagsDoc, automations };
  } catch (error) {
    return {
      tagsDoc: undefined,
      automations: [],
      fetchError:
        error instanceof Error
          ? error.message
          : "Failed to load tags/automations",
    };
  }
};

export const useShopConfigAuditRows = (shops: Shop[]) => {
  const shopIds = shops.map((s) => s.id);

  // Only the tags/automations reads are cached by shop id. Everything else is
  // derived from the shop documents below, on every change to them, so a shop
  // an operator just fixed never shows stale drift behind an unchanged id.
  const lists = useQuery({
    queryKey: ["shop-config-audit-lists", shopIds.join(",")],
    queryFn: async (): Promise<ShopLists[]> =>
      Promise.all(shops.map(fetchShopLists)),
    enabled: shops.length > 0,
    refetchOnMount: "always",
    staleTime: 5 * 60 * 1000,
    // Without this, an empty shop list leaves the query disabled and `data`
    // stays `undefined` (react-query never runs a disabled query), so
    // `data` would be `undefined`, not `[]`, on an empty page.
    initialData: shops.length === 0 ? [] : undefined,
  });

  const settings = useMemo(
    () =>
      shops.map((shop) => ({
        shop,
        shopConfig: auditShopConfig(shop),
        cardColors: auditCardColors(shop),
        enabledFeatures: auditEnabledFeatures(shop),
      })),
    [shops]
  );

  const data = useMemo((): ShopAuditRow[] | undefined => {
    if (!lists.data) return undefined;
    return settings.map((row, i): ShopAuditRow => {
      const { tagsDoc, automations, fetchError } = lists.data[i] ?? {
        tagsDoc: undefined,
        automations: [],
      };
      return {
        ...row,
        tags: auditTags(tagsDoc),
        automations: auditAutomations(automations),
        ...(fetchError ? { fetchError } : {}),
      };
    });
  }, [settings, lists.data]);

  return { data, isLoading: lists.isLoading, isError: lists.isError };
};
